import * as XLSX from 'xlsx';

// Column definitions for each importable entity type — field names mirror
// what the corresponding backend seed file (src/seed/*.js) actually expects,
// so a filled-in template maps directly onto real create/update logic once
// the backend import endpoint exists.
// Each column carries a `width` (px) sized to what it actually holds — short
// codes/booleans stay narrow, free-text fields get real room — so the
// preview table (which uses these as fixed column widths) doesn't truncate
// everything down to a few characters regardless of content.
export const ENTITY_TYPES = [
  {
    value: 'categories',
    label: 'Categories',
    description: 'Shop/service categories (maps to src/seed/categorySeed.js)',
    columns: [
      { key: 'name', label: 'Name', required: true, example: 'Mobile App Development', width: 200 },
      { key: 'title', label: 'Title', required: true, example: 'Mobile App Development Services', width: 220 },
      { key: 'slug', label: 'Slug', required: true, example: 'mobile-app-development', width: 180 },
      { key: 'shortDescription', label: 'Short Description', required: false, example: 'Professional mobile app development', width: 240 },
      { key: 'description', label: 'Description', required: false, example: 'Comprehensive mobile app development services...', width: 280 },
      { key: 'imageUrl', label: 'Image URL', required: false, example: 'https://example.com/image.jpg', width: 200 },
      { key: 'categoryTypeSlug', label: 'Category Type Slug', required: true, example: 'start', width: 150 },
      { key: 'active', label: 'Active (true/false)', required: false, example: 'true', width: 110 },
    ],
  },
  {
    value: 'valuepacks',
    label: 'Value Packs',
    description: 'Value pack info (maps to createValuePack) — pricing tiers are configured separately in the admin UI after import. Target Industries must be one of: ecommerce, saas, education, healthcare, real-estate, finance, travel, food-delivery.',
    columns: [
      { key: 'name', label: 'Name', required: true, example: 'Startup Growth Pack', width: 200 },
      { key: 'subTitle', label: 'Sub Title', required: true, example: 'Launch your business online', width: 220 },
      { key: 'slug', label: 'Slug', required: true, example: 'startup-growth-pack', width: 180 },
      { key: 'shortDescription', label: 'Short Description', required: true, example: 'Everything a new business needs to launch online', width: 260 },
      { key: 'description', label: 'Description', required: true, example: 'A complete package combining website development, branding, and marketing essentials to help new businesses establish a strong online presence from day one.', width: 300 },
      { key: 'categorySlugs', label: 'Category Slugs', required: false, example: 'website-development,digital-marketing', width: 240 },
      { key: 'targetIndustries', label: 'Target Industries', required: true, example: 'ecommerce,saas', width: 190 },
      { key: 'imageUrl', label: 'Image URL', required: false, example: 'https://example.com/image.jpg', width: 200 },
      { key: 'active', label: 'Active (true/false)', required: false, example: 'true', width: 110 },
    ],
  },
  {
    value: 'products',
    label: 'Products',
    description: 'Shop products with base tier pricing (maps to src/seed/products.seed.js)',
    columns: [
      { key: 'name', label: 'Name', required: true, example: 'Basic iOS App Development', width: 220 },
      { key: 'description', label: 'Description', required: false, example: 'Essential iOS app development for small businesses', width: 280 },
      { key: 'url', label: 'URL Slug', required: true, example: 'basic-ios-app-development', width: 200 },
      { key: 'categoryName', label: 'Category Name', required: true, example: 'Mobile App Development', width: 200 },
      { key: 'imageUrl', label: 'Image URL', required: false, example: 'https://example.com/image.jpg', width: 200 },
      { key: 'priceLow', label: 'Price (Low)', required: true, example: '49', width: 110 },
      { key: 'priceMid', label: 'Price (Mid)', required: true, example: '99', width: 110 },
      { key: 'priceHigh', label: 'Price (High)', required: true, example: '199', width: 110 },
      { key: 'active', label: 'Active (true/false)', required: false, example: 'true', width: 110 },
    ],
  },
  {
    value: 'countries',
    label: 'Countries',
    description: 'Country/currency reference data (maps to src/seed/countrySeed.js)',
    columns: [
      { key: 'countryCode', label: 'Country Code', required: true, example: 'US', width: 130 },
      { key: 'countryName', label: 'Country Name', required: true, example: 'United States', width: 180 },
      { key: 'displayName', label: 'Display Name', required: false, example: 'United States', width: 180 },
      { key: 'currencyCode', label: 'Currency Code', required: true, example: 'USD', width: 140 },
      { key: 'currencySymbol', label: 'Currency Symbol', required: true, example: '$', width: 140 },
      { key: 'priceMultiplier', label: 'Price Multiplier', required: false, example: '1.0', width: 150 },
      { key: 'taxRate', label: 'Tax Rate', required: false, example: '0.08', width: 120 },
      { key: 'phoneCode', label: 'Phone Code', required: false, example: '+1', width: 130 },
      { key: 'isDefault', label: 'Is Default (true/false)', required: false, example: 'true', width: 150 },
      { key: 'displayOrder', label: 'Display Order', required: false, example: '1', width: 140 },
      { key: 'isActive', label: 'Active (true/false)', required: false, example: 'true', width: 110 },
    ],
  },
  {
    value: 'durations',
    label: 'Durations',
    description: 'Plan/subscription duration options (maps to src/seed/duration.seed.js)',
    columns: [
      { key: 'name', label: 'Name', required: true, example: '3 Month Plan', width: 180 },
      { key: 'value', label: 'Value', required: true, example: '3', width: 100 },
      { key: 'unit', label: 'Unit (day/month/year)', required: true, example: 'month', width: 160 },
      { key: 'description', label: 'Description', required: false, example: 'Quarterly subscription', width: 240 },
      { key: 'displayOrder', label: 'Display Order', required: false, example: '1', width: 140 },
      { key: 'isDefault', label: 'Is Default (true/false)', required: false, example: 'false', width: 150 },
      { key: 'isActive', label: 'Active (true/false)', required: false, example: 'true', width: 110 },
    ],
  },
];

export const getEntityConfig = (value) => ENTITY_TYPES.find((e) => e.value === value) || null;

// Build and trigger download of a template workbook for the given entity — a
// header row plus one example row, so the admin knows exactly what each
// column expects without needing to read backend code first.
export const downloadEntityTemplate = (entityConfig) => {
  const headers = entityConfig.columns.map((c) => c.label);
  const example = entityConfig.columns.map((c) => c.example ?? '');
  const sheet = XLSX.utils.aoa_to_sheet([headers, example]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, entityConfig.label.slice(0, 31));
  XLSX.writeFile(workbook, `${entityConfig.value}-import-template.xlsx`);
};

// Validate one parsed row against the entity's column definitions. Only
// checks presence for required fields — deeper checks (e.g. does
// categoryTypeSlug actually exist) need the backend, which has the DB.
export const validateImportRow = (row, entityConfig) => {
  const errors = [];
  entityConfig.columns.forEach((col) => {
    if (col.required) {
      const value = row[col.label];
      if (value === undefined || value === null || String(value).trim() === '') {
        errors.push(`Missing required field: ${col.label}`);
      }
    }
  });
  return errors;
};

// Parse an uploaded Excel/CSV file (SheetJS handles both) into row objects
// keyed by header label, ready for validateImportRow.
export const parseImportFile = (arrayBuffer) => {
  const workbook = XLSX.read(new Uint8Array(arrayBuffer), { type: 'array' });
  const firstSheetName = workbook.SheetNames[0];
  const sheet = workbook.Sheets[firstSheetName];
  return XLSX.utils.sheet_to_json(sheet, { defval: '' });
};

// Human-readable file size for the upload zone (e.g. "48 KB") so a non-
// technical user gets a sanity check that the right file was picked.
export const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
